import { useEffect } from 'react';

interface UseScrollToBottomDetectionOptions {
  scrollAreaRef: React.RefObject<HTMLDivElement | null>;
  onScrolledToBottom?: () => void;
  hasScrolledToBottom: boolean;
}

export function useScrollToBottomDetection({
  scrollAreaRef,
  onScrolledToBottom,
  hasScrolledToBottom,
}: UseScrollToBottomDetectionOptions) {
  useEffect(() => {
    if (hasScrolledToBottom) return;

    const container = scrollAreaRef.current;
    if (!container) return;

    const viewport = container.querySelector(
      '[data-slot="scroll-area-viewport"]',
    ) as HTMLElement | null;
    if (!viewport) return;

    const checkIfAtBottom = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const scrollThreshold = 20;

      const noScrollNeeded = scrollHeight <= clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - scrollThreshold;

      if ((noScrollNeeded || isAtBottom) && onScrolledToBottom) {
        onScrolledToBottom();
      }
    };

    viewport.addEventListener('scroll', checkIfAtBottom);

    // Check on mount and after a short delay to ensure content is rendered
    checkIfAtBottom();
    const timeoutId = setTimeout(checkIfAtBottom, 100);

    return () => {
      viewport.removeEventListener('scroll', checkIfAtBottom);
      clearTimeout(timeoutId);
    };
  }, [scrollAreaRef, onScrolledToBottom, hasScrolledToBottom]);
}
